// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, InputLabel, Radio, RadioGroup,
} from '@material-ui/core';

// Internal Dependencies
import { Status } from '../../../common/constants';
import { usePromoCode } from '../../../hook/usePromoCode';
import { IsArray, IsEmpty } from '../../../common/checks';

const Mode = {
  Single:   'Single',
  Multiple: 'Multiple',
};

const UpdatePromoCodeModal = (props) => {
  const { onPromoCodeUpdate, onBulkUpdatePromoCodes } = usePromoCode();

  const [status, setStatus] = useState(null);

  const handlePromoCodeUpdate = async () => {
    try {
      if (props.mode === Mode.Single) {
        await onPromoCodeUpdate(props.selectedPromoCode.id, { status });
        props.onClose();
      } else if (props.mode === Mode.Multiple) {
        await onBulkUpdatePromoCodes(props.selectedPromoCodesId, { status });
        props.onClose();
      }
      props.onComplete();
    } catch (error) {
      toast.error('Failed to update promo codes');
      toast.error(error.message);
    }
  };

  useEffect(() => {
    setStatus(null);
  }, [props.selectedPromoCode, props.mode]);

  if (
    (props.mode === Mode.Single && IsEmpty(props.selectedPromoCode)) || (props.mode === Mode.Multiple && !IsArray(props.selectedPromoCodesId))
  ) {
    return null;
  }

  return (
    <Dialog
      open={props.isModalOpen}
      fullWidth
      maxWidth="xs"
      onClose={props.onClose}
    >
      <DialogTitle>Update promo code</DialogTitle>
      <DialogContent>
        <div>
          <div>
            <FormControl style={{ width: '100%' }}>
              <InputLabel shrink>
                Status
              </InputLabel>
              <RadioGroup
                row
                aria-label="status"
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-3"
              >
                <FormControlLabel
                  value={Status.Deactivated}
                  control={<Radio color="secondary" />}
                  label={Status.Deactivated}
                />
                <FormControlLabel
                  className="ml-5"
                  value={Status.Redeemed}
                  control={<Radio color="primary" />}
                  label={Status.Redeemed}
                />
              </RadioGroup>
            </FormControl>
          </div>
          {
            !IsEmpty(props.numberOfSelectedRecords) && (
              <div className="mt-3">
                {props.numberOfSelectedRecords}
                {' '}
                promo codes selected
              </div>
            )
          }
        </div>
      </DialogContent>
      <DialogActions style={{
        padding: '16px 24px 16px 24px',
      }}
      >
        <div className="d-flex flex-row justify-content-end" style={{ columnGap: 12 }}>
          <Button
            color="white"
            variant="contained"
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={IsEmpty(status)}
            color="primary"
            variant="contained"
            onClick={handlePromoCodeUpdate}
          >
            Update
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

UpdatePromoCodeModal.propTypes = {
  selectedPromoCode: PropTypes.shape({
    id:     PropTypes.number.isRequired,
    status: PropTypes.oneOf([
      Status.Activated,
      Status.Deactivated,
      Status.Redeemed,
      Status.Unredeemed,
    ]).isRequired,
  }),
  selectedPromoCodesId:    PropTypes.arrayOf(PropTypes.number),
  isModalOpen:             PropTypes.bool.isRequired,
  onClose:                 PropTypes.func.isRequired,
  onComplete:              PropTypes.func,
  mode:                    PropTypes.oneOf([Mode.Single, Mode.Multiple]).isRequired,
  numberOfSelectedRecords: PropTypes.number,
};

UpdatePromoCodeModal.defaultProps = {
  selectedPromoCode:       {},
  selectedPromoCodesId:    [],
  numberOfSelectedRecords: null,
  onComplete:              () => { },
};

export default UpdatePromoCodeModal;
